// Web 应用更新服务
import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';

export interface UpdateInfo {
  hasUpdate: boolean;
  currentVersion: string;
  latestVersion: string;
  updateUrl?: string;
  forceUpdate?: boolean;
  releaseNotes?: string;
}

/**
 * 获取当前应用版本
 */
export async function getCurrentVersion(): Promise<string> {
  // Web 环境，从 package.json 读取或使用默认值
  // 注意：在生产环境中，可以通过环境变量或构建时注入版本号
  try {
    // 尝试从环境变量读取版本号
    const version = (import.meta as unknown as { env?: { VITE_APP_VERSION?: string } }).env?.VITE_APP_VERSION;
    if (version) {
      return version;
    }
  } catch (error) {
    console.debug('[UpdateService] 无法从环境变量读取版本号');
  }
  
  // 默认版本号
  return '1.0.0';
}

/**
 * 比较两个版本号
 * @param v1 版本号1
 * @param v2 版本号2
 * @returns 1: v1 > v2, -1: v1 < v2, 0: v1 === v2
 */
function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const part1 = parts1[i] || 0;
    const part2 = parts2[i] || 0;
    
    if (part1 > part2) return 1;
    if (part1 < part2) return -1;
  }
  
  return 0;
}

/**
 * 检查是否有更新
 */
export async function checkForUpdates(): Promise<UpdateInfo> {
  const currentVersion = await getCurrentVersion();
  
  console.log('[UpdateService] 开始检查更新');
  console.log('[UpdateService] 当前版本:', currentVersion);
  
  // 检查 Supabase 是否可用
  if (!isSupabaseAvailable()) {
    console.error('[UpdateService] ❌ Supabase 配置缺失！');
    return {
      hasUpdate: false,
      currentVersion,
      latestVersion: currentVersion,
    };
  }

  if (!supabase) {
    console.error('[UpdateService] ❌ Supabase 客户端未初始化！');
    return {
      hasUpdate: false,
      currentVersion,
      latestVersion: currentVersion,
    };
  }
  
  try {
    // 使用 Supabase 客户端调用 Edge Function
    const { data, error } = await supabase.functions.invoke('check-update', {
      body: {
        platform: 'web',
        currentVersion,
      },
    });
    
    if (error) {
      console.error('[UpdateService] Edge Function 调用失败:', error);
      throw new Error(`版本检查失败: ${error.message || JSON.stringify(error)}`);
    }
    
    console.log('[UpdateService] 响应数据:', JSON.stringify(data, null, 2));
    
    // 验证返回的数据
    if (!data.latestVersion) {
      throw new Error('版本检查 API 返回数据格式错误');
    }
    
    // 优先使用 API 返回的 hasUpdate 字段
    let hasUpdate: boolean;
    if (typeof data.hasUpdate === 'boolean') {
      hasUpdate = data.hasUpdate;
    } else {
      // API 没有返回 hasUpdate，自己比较版本号
      const versionCompareResult = compareVersions(data.latestVersion, currentVersion);
      hasUpdate = versionCompareResult > 0;
    }
    
    const result = {
      hasUpdate,
      currentVersion,
      latestVersion: data.latestVersion || currentVersion,
      updateUrl: data.updateUrl,
      forceUpdate: data.forceUpdate || false,
      releaseNotes: data.releaseNotes,
    };
    
    console.log('[UpdateService] 返回结果:', JSON.stringify(result, null, 2));
    
    return result;
  } catch (error) {
    console.error('[UpdateService] 检查更新失败:', error);
    // 发生错误时，返回无更新的信息，避免影响应用正常使用
    return {
      hasUpdate: false,
      currentVersion,
      latestVersion: currentVersion,
    };
  }
}

/**
 * 打开应用商店或下载页面
 */
export async function openUpdateUrl(updateUrl?: string): Promise<void> {
  if (!updateUrl) {
    console.warn('[UpdateService] 无法确定更新 URL');
    return;
  }
  
  if (typeof window !== 'undefined') {
    window.open(updateUrl, '_blank');
  } else {
    console.warn('[UpdateService] window 对象不可用，无法打开更新链接');
  }
}

/**
 * 下载进度回调类型（Web 环境不需要，但保留接口兼容性）
 */
export type DownloadProgressCallback = (progress: {
  progress: number; // 0-100
  bytesRead: number;
  totalBytes: number;
}) => void;

/**
 * 下载并安装 APK（Web 环境不支持，回退到打开浏览器）
 */
export async function downloadAndInstallApk(
  updateUrl: string,
  _fileName?: string,
  _onProgress?: DownloadProgressCallback
): Promise<void> {
  // Web 环境不支持应用内更新，直接打开浏览器下载
  console.log('[UpdateService] Web 环境不支持应用内更新，使用浏览器下载');
  await openUpdateUrl(updateUrl);
}
